import { useCopy } from '@/copy';
import { DisplayScreen } from '@/pages/DisplayScreen';

/**
 * The partner's own first screen, rendered but frozen, for a brand locked
 * with `widgetLockScreen: FROZEN_FIRST_SCREEN`.
 *
 * The alternative to the holding screen. A partner who wants their embed to
 * look finished on the page before they have paid gets their journey's
 * opening screen, in their own colours, with their own defaults — and
 * nothing on it works. No field takes a keystroke, no slider moves, no
 * button presses, and the journey never starts.
 *
 * **Whatever that screen happens to be.** This renders `DisplayScreen`, not
 * a named screen, so it shows whatever `getInitialDisplayScreen` resolved
 * for this partner during bootstrap: the quick calculator for CarExpert,
 * the Truckstop landing page under that user flow, the multi-asset tiles
 * for a brand running them, the no-lender home when the brand has no quick
 * quote. Naming one screen here would have frozen the right thing for
 * CarExpert and the wrong thing for everyone else. Nothing can navigate, so
 * the display never moves off that first screen — DisplayScreen renders the
 * same thing for the life of the session.
 *
 * **Frozen, not disabled-looking.** Every control keeps its normal styling:
 * the point is a screen that reads as the finished product, not one that
 * reads as broken. Deliberately no "coming soon" notice either — that is
 * what the holding screen is for, and a partner who wanted it would have
 * chosen it.
 *
 * Three things hold it still, because one is not enough:
 *   - `fieldset[disabled]` — the only one browsers enforce on the controls
 *     themselves. Inputs, selects and buttons inside stop taking keyboard
 *     input and stop firing change and click events.
 *   - `pointer-events-none` — these screens are full of divs with onClick
 *     (the asset tiles, the condition toggle), which a disabled fieldset
 *     does nothing for.
 *   - `inert` — takes the whole subtree out of the tab order and hides it
 *     from assistive tech, so nobody can reach a control by keyboard. React
 *     18 has no typed prop for it, hence the spread.
 *
 * The one thing that DOES run is the initial quick quote, where the first
 * screen is one that quotes, so the estimate shows a real figure rather
 * than the dash an unquoted card renders. With every input frozen it can
 * never be recalculated, so the number is static by construction. If this
 * should make no network call at all, guard `getQuickQuote` the way `emit`
 * is guarded in `uniqueIdTransfer` and the card will show "-".
 *
 * Tracking is suppressed for any locked widget — see `emit`. These screens
 * report a view through DisplayComponent on mount; letting that through
 * would put sessions that cannot convert into the funnel and quietly drop
 * that brand's conversion rate. It matches the holding screen, which
 * deliberately reports nothing.
 *
 * Mirrors RealApp's container markup rather than reusing it, minus the SSE
 * subscriptions, the modal layer and the review nav: none of them can be
 * reached from a frozen screen, and the lock exists to stop them mounting.
 * Dropping the modal layer is deliberate rather than incidental — bootstrap
 * can queue a register modal for some user flows, and a locked widget
 * popping a sign-up form over a screen that cannot submit would be worse
 * than not showing it.
 */

export const testId = 'WidgetLockedFirstScreen';

export function WidgetLockedFirstScreen(): JSX.Element {
    const { cardGround } = useCopy();

    return (
        <div
            className='w-full h-full flex-center'
            data-testid={testId}
        >
            <div
                className={`h-full w-full max-w-[394px] flex-center overflow-x-hidden relative ${
                    cardGround ? 'bg-pageGround' : 'bg-white'
                }`}
            >
                <div className='mx-auto h-full w-full max-w-md _flex flex-col relative'>
                    {/* Scrolls, so a short embed can still see the whole
                        screen. Reading it is the only thing left to do. */}
                    <div className='flex-grow overflow-y-auto'>
                        <fieldset
                            disabled
                            className='_flex m-0 h-full w-full min-w-0 flex-col border-0 p-0 pointer-events-none select-none'
                            {...({ inert: '' } as { inert?: string })}
                        >
                            <DisplayScreen />
                        </fieldset>
                    </div>
                </div>
            </div>
        </div>
    );
}
